// Psychometric profile (epic 71) — value & moral priors that help the twin
// cold-start on topics with no matching memory. Consent-gated, owner-only.
// Two ways to build it: fill the surveys (PVQ-21 / MFQ-30), or infer it passively
// from the vault + past verdicts. A recorded stance always overrides the prior.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface Dim { value: number; sem: number; source: 'survey' | 'passive' }
interface Status {
  consent: boolean;
  hasSchwartz: boolean; hasMoral: boolean;
  schwartzSource: string | null; moralSource: string | null;
  dimCount: number; reliable: number; sources: { survey: number; passive: number };
  completeness: number; retainRaw: boolean; updatedAt: string;
  dims: Record<string, Dim>;
  weakest: { dim: string; label: string; instrument: 'schwartz' | 'moral' } | null;
}

const LABEL: Record<string, string> = {
  schwartz_self_direction: 'Autonomie', schwartz_stimulation: 'Stimulation', schwartz_hedonism: 'Hédonisme',
  schwartz_achievement: 'Accomplissement', schwartz_power: 'Pouvoir', schwartz_security: 'Sécurité',
  schwartz_conformity: 'Conformité', schwartz_tradition: 'Tradition', schwartz_benevolence: 'Bienveillance',
  schwartz_universalism: 'Universalisme',
  mf_care: 'Soin', mf_fairness: 'Équité', mf_loyalty: 'Loyauté', mf_authority: 'Autorité', mf_purity: 'Pureté',
};

export function PsychometricsPane() {
  const qc = useQueryClient();
  const [survey, setSurvey] = useState<'schwartz' | 'moral' | null>(null);
  const statusQ = useQuery<{ status: Status }>({ queryKey: ['psychometrics', 'status'], queryFn: () => api('/api/psychometrics/status') });
  const s = statusQ.data?.status;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['psychometrics'] });
  const consent = useMutation({ mutationFn: (granted: boolean) => api('/api/psychometrics/consent', { method: 'POST', body: JSON.stringify({ granted }) }), onSuccess: invalidate });
  const [msg, setMsg] = useState<string | null>(null);
  const passive = useMutation({
    mutationFn: () => api('/api/psychometrics/passive', { method: 'POST' }),
    onMutate: () => setMsg('Estimation depuis tes souvenirs…'),
    onSuccess: () => { setMsg('Profil estimé ✓ (indicatif — remplis les questionnaires pour préciser).'); invalidate(); },
    onError: () => setMsg('Indisponible — mode API requis, ou pas encore assez de souvenirs/verdicts.'),
  });
  const retain = useMutation({ mutationFn: (r: boolean) => api('/api/psychometrics/retain-raw', { method: 'POST', body: JSON.stringify({ retain: r }) }), onSuccess: invalidate });
  const del = useMutation({ mutationFn: () => api('/api/psychometrics', { method: 'DELETE' }), onSuccess: () => { setMsg('Profil supprimé.'); invalidate(); } });

  const top = useMemo(() => {
    if (!s) return { high: [] as string[], mf: [] as string[] };
    const sv = Object.entries(s.dims).filter(([k, d]) => k.startsWith('schwartz_') && !k.includes('_change') && !k.includes('enhancement') && !k.includes('conservation') && !k.includes('transcendence') && d.sem <= 0.3);
    const high = sv.filter(([, d]) => d.value >= 0.58).sort((a, b) => b[1].value - a[1].value).slice(0, 3).map(([k]) => LABEL[k] ?? k);
    const mf = Object.entries(s.dims).filter(([k, d]) => k.startsWith('mf_') && d.value >= 0.6 && d.sem <= 0.3).sort((a, b) => b[1].value - a[1].value).map(([k]) => LABEL[k] ?? k);
    return { high, mf };
  }, [s]);

  return (
    <div className="grid grid-cols-[160px_1fr] gap-5 items-start py-3 border-b border-ink/[0.06]">
      <div className="text-[11px] text-ink/45 pt-2">Profil psycho.</div>
      <div className="space-y-3">
        <p className="font-serif italic text-[12px] text-ink/50 max-w-prose">
          Valeurs (Schwartz) & fondations morales — un a-priori qui aide le twin quand aucun souvenir ne couvre un sujet.
          Données sensibles, privées : rien n’est utilisé sans ton consentement, et le twin ne les cite jamais.
        </p>

        {/* Consent gate */}
        <label className="flex items-center gap-2 text-[12.5px] text-ink/70">
          <input type="checkbox" checked={Boolean(s?.consent)} onChange={(e) => consent.mutate(e.target.checked)} />
          J’autorise le twin à utiliser mon profil psychométrique (a-priori dans ses réponses).
        </label>

        {s && s.consent && (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-[12px] text-ink/55">Complétude fiable : <b className="text-ink/80">{s.completeness}%</b>
                <span className="text-ink/35"> · {s.sources.survey} par questionnaire, {s.sources.passive} estimés</span>
              </div>
            </div>

            {(top.high.length > 0 || top.mf.length > 0) && (
              <div className="rounded-xl border border-particle/20 bg-particle/[0.03] p-3 space-y-1 text-[12.5px]">
                {top.high.length > 0 && <div className="text-ink/70">Valeurs fortes : <span className="text-particle/90">{top.high.join(', ')}</span></div>}
                {top.mf.length > 0 && <div className="text-ink/70">Fondations morales : <span className="text-particle/90">{top.mf.join(', ')}</span></div>}
              </div>
            )}

            {s.weakest && (
              <p className="font-serif italic text-[12px] text-amber/75">
                À préciser : <b>{s.weakest.label}</b> (peu fiable) — remplis le questionnaire {s.weakest.instrument === 'moral' ? 'Morale (MFQ-30)' : 'Valeurs (PVQ-21)'}.
              </p>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <SurveyBtn label="Valeurs (PVQ-21)" done={s.hasSchwartz && s.schwartzSource === 'survey'} onClick={() => setSurvey('schwartz')} />
              <SurveyBtn label="Morale (MFQ-30)" done={s.hasMoral && s.moralSource === 'survey'} onClick={() => setSurvey('moral')} />
              <button onClick={() => passive.mutate()} disabled={passive.isPending}
                className="px-3 py-1.5 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink disabled:opacity-40">
                {passive.isPending ? '…' : '✨ Estimer depuis mes souvenirs'}
              </button>
            </div>

            <div className="flex items-center gap-3 flex-wrap pt-1">
              <label className="flex items-center gap-1.5 text-[11px] text-ink/45">
                <input type="checkbox" checked={s.retainRaw} onChange={(e) => retain.mutate(e.target.checked)} />
                conserver mes réponses brutes (pour re-scorer)
              </label>
              <button onClick={() => { if (confirm('Supprimer tout le profil psychométrique (réponses + scores) ?')) del.mutate(); }}
                className="ml-auto px-3 py-1 rounded-full border border-red-300/30 text-[11px] text-red-300/70 hover:text-red-300">
                Supprimer le profil
              </button>
            </div>
          </>
        )}
        {msg && <p className="font-serif italic text-[12px] text-ink/55">{msg}</p>}
      </div>

      {survey && <SurveyModal name={survey} onClose={() => setSurvey(null)} onSaved={() => { setSurvey(null); setMsg('Questionnaire enregistré ✓'); invalidate(); }} />}
    </div>
  );
}

function SurveyBtn({ label, done, onClick }: { label: string; done: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className={`px-3.5 py-1.5 rounded-full text-[11.5px] border ${done ? 'border-emerald-300/40 text-emerald-300 bg-emerald-300/[0.06]' : 'bg-amber text-void border-amber hover:bg-particle'}`}>
      {done ? `✓ ${label}` : label}
    </button>
  );
}

// ── Survey modal — a Likert renderer for both instruments ────────────────────
interface Section { prompt: string; scale: { min: number; max: number; minLabel: string; maxLabel: string }; items: { id: string; text: string }[] }
interface Bank { instrument: string; meta: { label: string }; scale?: Section['scale']; items?: { id: string; text: string }[]; parts?: { prompt: string; scale: Section['scale']; items: { id: string; text: string }[] }[] }

function SurveyModal({ name, onClose, onSaved }: { name: 'schwartz' | 'moral'; onClose: () => void; onSaved: () => void }) {
  const bankQ = useQuery<Bank>({ queryKey: ['psychometrics', 'instrument', name], queryFn: () => api(`/api/psychometrics/instrument/${name}`) });
  const [resp, setResp] = useState<Record<string, number>>({});
  const submit = useMutation({
    mutationFn: () => api(`/api/psychometrics/survey/${name}`, { method: 'POST', body: JSON.stringify({ responses: resp }) }),
    onSuccess: onSaved,
  });

  const sections: Section[] = useMemo(() => {
    const b = bankQ.data; if (!b) return [];
    if (b.parts) return b.parts;
    return [{ prompt: 'À quel point cette description te ressemble-t-elle ?', scale: b.scale!, items: b.items! }];
  }, [bankQ.data]);
  const total = sections.reduce((n, s) => n + s.items.length, 0);
  const answered = Object.keys(resp).length;

  return (
    <div className="fixed inset-0 z-[130] grid place-items-center bg-black/65 backdrop-blur-md p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-[min(720px,96vw)] max-h-[90vh] overflow-hidden flex flex-col rounded-2xl border border-ink/15 bg-void">
        <div className="px-5 py-3 border-b border-ink/10 flex items-center justify-between">
          <h3 className="font-serif text-xl text-ink">{bankQ.data?.meta.label ?? '…'}</h3>
          <span className="text-[11.5px] text-ink/45">{answered}/{total}</span>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {bankQ.isLoading ? <div className="text-ink/40 text-[12px]">Chargement…</div> : sections.map((sec, si) => (
            <div key={si} className="space-y-3">
              <p className="font-serif italic text-[13px] text-ink/60">{sec.prompt}</p>
              {sec.items.map((it) => (
                <div key={it.id} className="space-y-1.5">
                  <p className="font-serif text-[14.5px] text-ink/90 leading-snug">{it.text}</p>
                  <Likert scale={sec.scale} value={resp[it.id]} onChange={(v) => setResp((r) => ({ ...r, [it.id]: v }))} />
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-ink/10 flex items-center gap-3">
          <button onClick={() => submit.mutate()} disabled={answered < total || submit.isPending}
            className="px-5 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30">
            {submit.isPending ? 'Enregistrement…' : answered < total ? `Réponds aux ${total - answered} restantes` : 'Enregistrer'}
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink">Fermer</button>
        </div>
      </div>
    </div>
  );
}

function Likert({ scale, value, onChange }: { scale: Section['scale']; value?: number; onChange: (v: number) => void }) {
  const opts: number[] = [];
  for (let i = scale.min; i <= scale.max; i++) opts.push(i);
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-ink/35 w-24 text-right shrink-0">{scale.minLabel}</span>
      <div className="flex items-center gap-1.5">
        {opts.map((o) => (
          <button key={o} onClick={() => onChange(o)}
            className={`w-8 h-8 rounded-full border text-[12px] transition-colors ${value === o ? 'border-amber bg-amber text-void' : 'border-ink/20 text-ink/50 hover:border-ink/40'}`}>
            {o}
          </button>
        ))}
      </div>
      <span className="text-[10px] text-ink/35 w-24 shrink-0">{scale.maxLabel}</span>
    </div>
  );
}
